<script setup lang="ts">
import type { Component } from 'vue';
import UiCard from '@krafta/ui-kit/components/UiCard.vue';
import UiSideNav from '@krafta/ui-kit/components/UiSideNav.vue';

type NavLink = {
  to: string;
  label: string;
  icon?: Component;
};

defineProps<{
  navLinks: NavLink[];
  themeClasses: {
    surface: string;
    muted: string;
  };
  isNavLinkActive: (to: string) => boolean;
  onNavigate: () => void;
}>();
</script>

<template>
  <UiCard
    padding="sm"
    :class="['mobile-navigation', themeClasses.surface]"
  >
    <header class="mobile-navigation__header">
      <p
        class="mobile-navigation__eyebrow"
        :class="themeClasses.muted"
      >
        Навигация
      </p>
      <span
        class="mobile-navigation__count"
        aria-hidden="true"
      >
        {{ navLinks.length }}
      </span>
    </header>
    <UiSideNav
      class="mobile-navigation__list"
      orientation="grid"
      density="compact"
      :items="navLinks"
      aria-label="Основная навигация"
      :is-nav-link-active="isNavLinkActive"
      @click="onNavigate"
    />
  </UiCard>
</template>

<style scoped>
.mobile-navigation {
  border-color: color-mix(in srgb, var(--ui-border) 86%, transparent);
  background:
    radial-gradient(circle at 16% 0%, color-mix(in srgb, var(--ui-accent) 10%, transparent) 0, transparent 36%),
    var(--ui-surface);
}

.mobile-navigation__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ui-space-3);
  padding: 0 0.125rem var(--ui-space-3);
}

.mobile-navigation__eyebrow {
  margin: 0;
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  line-height: 0.875rem;
  text-transform: uppercase;
}

.mobile-navigation__count {
  display: inline-flex;
  min-width: 1.625rem;
  height: 1.625rem;
  align-items: center;
  justify-content: center;
  border-radius: var(--ui-radius-full);
  background: color-mix(in srgb, var(--ui-accent) 12%, var(--ui-surface));
  color: var(--ui-accent);
  font-size: 0.75rem;
  font-weight: 800;
}

.mobile-navigation__list {
  margin-top: 0;
}

:deep(.ui-side-nav--grid .ui-side-nav__list) {
  grid-template-columns: repeat(auto-fit, minmax(min(12rem, 100%), 1fr));
  gap: var(--ui-space-2);
}

:deep(.ui-side-nav--compact .ui-side-nav__link) {
  min-height: 2.875rem;
  border: 1px solid color-mix(in srgb, var(--ui-border) 72%, transparent);
  border-radius: var(--ui-radius-md);
  background: color-mix(in srgb, var(--ui-surface) 92%, transparent);
}

:deep(.ui-side-nav__link--active) {
  border-color: color-mix(in srgb, var(--ui-accent) 30%, var(--ui-border));
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--ui-accent) 12%, transparent);
}
</style>
